"use client"

import styled from "@emotion/styled"
import { Button, TextField, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { postTrafficUpdateAll } from "api"
import debounce from "debounce"
import { useTranslations } from "next-intl"
import dynamic from "next/dynamic"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { TransitionUp, useInViewConfig } from "ui"
import Container from "ui/Container"
import CloseXIcon from "ui/icons/CloseXIcon"
import Search from "ui/icons/Search"
import { sharedColors } from "ui/theme/colors"
import { delayTime } from "ui/transitions/utils"
import {
  TrafficUpdate,
  transformApiToTrafficUpdate,
} from "utils/transformUpdate"
import TraffixBox from "../Homepage/TrafficUpdates/TraffixBox"
import cactus from "./cactus.json"
const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
`

const InnerContainer = styled.div<{ $isData: boolean }>`
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-bottom: 32px;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
    align-self: center;
  }
`

const StyledLottie = styled(Lottie)`
  height: 120px;
  padding-top: 16px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 32px;
    align-self: baseline;
    height: 320px;
  }
  /* display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  } */
  /* position: absolute;
  bottom: 0;
  right: 0; */
`

const StyledTextField = styled(TextField)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 400px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    width: 500px;
  }
  svg[aria-label="close"] {
    cursor: pointer;
  }
  label {
    font-size: 16px;
    color: ${sharedColors.labelGray};
  }
  label[data-shrink="true"] {
    transform: translate(10px, -35px);
  }
`

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const APIContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 40px;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    padding: 0;
    padding-bottom: 64px;
  }
`

const StyledForm = styled.form`
  height: fit-content;
  width: fit-content;
  margin-top: 24px;
  margin-bottom: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin: 0;
  }
  width: 100%;
`

const StyledTypography = styled(Typography)`
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin-top: 48px;
  }
`

export default function TrafficUpdates({
  trafficUpdates,
}: {
  trafficUpdates: TrafficUpdate[]
}) {
  const tInner = useTranslations("InnerPages")
  const searchParams = useSearchParams()
  const searchParam = searchParams.get("search")
  const [inputValue, setInputValue] = useState(searchParam || "")
  const [visibleItems, setVisibleItems] = useState(9)
  const { ref, inView } = useInView(useInViewConfig)
  const router = useRouter()
  const pathname = usePathname()
  const newContentRef = useRef<HTMLDivElement>(null)
  const focusTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup focus timer on unmount
  useEffect(() => {
    return () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current)
      }
    }
  }, [])

  const handleDebouncedSearch = useCallback(
    debounce((value: string) => {
      const params = new URLSearchParams(searchParams)
      params.set("search", value)
      router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    }, 1000),
    [router, pathname, searchParams],
  )

  const { data: apiData } = useQuery({
    queryKey: ["trafficUpdates", searchParam],
    queryFn: async () => {
      const res = await postTrafficUpdateAll({
        pageSize: 10,
        requestPage: 0,
        searchTerm: inputValue,
        isHomePage: false,
      })
      const apiItems = res.data.body.trafficUpdates.items.map((el) =>
        transformApiToTrafficUpdate(el),
      )
      const umbracoItems = trafficUpdates.filter((el) => {
        if (!inputValue) return true
        return el.lines.some((line) => line.toString() === inputValue)
      })
      const items = [...umbracoItems, ...apiItems]
      const sortedItems = items.sort((a, b) => {
        const dateA = new Date(a.activePeriodStart)
        const dateB = new Date(b.activePeriodStart)
        return dateB.getTime() - dateA.getTime()
      })
      return sortedItems
    },
    placeholderData: (prev) => prev,
  })
  const showCactus = apiData?.length === 0 && !!inputValue

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    handleDebouncedSearch(value)
  }

  const handleClear = () => {
    setInputValue("")
    handleDebouncedSearch("")
  }

  const handleLoadMore = () => {
    const currentVisibleItems = visibleItems
    setVisibleItems((prev) => prev + 9)

    // Clear any existing timer
    if (focusTimerRef.current) {
      clearTimeout(focusTimerRef.current)
    }

    // Focus on the first newly loaded item after state update
    focusTimerRef.current = setTimeout(() => {
      const newlyLoadedItem = document.querySelector(
        `[data-traffic-item-index="${currentVisibleItems}"]`,
      ) as HTMLElement
      if (newlyLoadedItem) {
        newlyLoadedItem.focus()
        newlyLoadedItem.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }
      focusTimerRef.current = null
    }, 100)
  }

  return (
    <>
      <Container paddingMobile={true}>
        <StyledContainer ref={ref}>
          <TransitionUp $inView={inView} $delay={delayTime}>
            <InnerContainer $isData>
              <StyledForm>
                <StyledTextField
                  name="search"
                  variant="filled"
                  label={tInner("enterLine")}
                  slotProps={{
                    input: {
                      endAdornment: inputValue ? (
                        <CloseXIcon onClick={handleClear} />
                      ) : (
                        <Search />
                      ),
                    },
                  }}
                  value={inputValue}
                  onChange={handleInputChange}
                />
              </StyledForm>
              {!!apiData?.length && (
                <StyledTypography variant="body2">
                  {(() => {
                    const translated = tInner("weFound20Updat")
                    const parts = translated.split("20")
                    return (
                      <>
                        {parts[0]}
                        <span style={{ fontWeight: 700 }}>
                          {apiData.length}
                        </span>
                        {parts[1]}
                      </>
                    )
                  })()}
                </StyledTypography>
              )}
              {showCactus ? (
                <StyledTypography variant="body2">
                  {tInner("noUpdatesFound")}
                </StyledTypography>
              ) : null}
              {showCactus && (
                <StyledLottie
                  animationData={cactus}
                  loop={true}
                  autoplay={true}
                />
              )}
            </InnerContainer>
          </TransitionUp>
        </StyledContainer>
      </Container>
      {!!apiData?.length && (
        <Container paddingMobile={true}>
          <APIContainer>
            <DataContainer role="list">
              {apiData.slice(0, visibleItems).map((el, i) => (
                <div
                  key={i}
                  data-traffic-item-index={i}
                  tabIndex={-1}
                  style={{ outline: "none" }}
                  role="listitem"
                >
                  <TraffixBox el={el} overflow={true} spaceBetween={true} />
                </div>
              ))}
            </DataContainer>
            {apiData.length > visibleItems && (
              <StyledButton variant="secondary" onClick={handleLoadMore}>
                {tInner("additionalUpdates")}
              </StyledButton>
            )}
          </APIContainer>
        </Container>
      )}
    </>
  )
}
